import React, { useState, useEffect } from 'react';

/**
 * LiveCourtCard - Tarjeta de Pista en Vivo
 * 
 * Componente mobile-first ultra-simplificado para gestionar una pista
 * durante un evento en vivo. Optimizado para uso a pie de pista.
 */
const LiveCourtCard = ({ court, courtNumber, onUpdateScore, onFinishMatch, onStartMatch }) => {
    // Si no hay datos del partido pero tenemos el número de pista, mostramos estado "Esperando"
    if (!court) {
        return (
            <div className="bg-[#1A1D2B] rounded-xl overflow-hidden shadow-[0_0_25px_rgba(255,255,255,0.12)] border border-[#252A3D] flex flex-col h-full animate-pulse">
                <div className="px-4 py-1.5 flex justify-between items-center bg-[#0F111A] border-b border-[#252A3D]">
                    <div className="flex items-center gap-1.5 font-black italic tracking-tighter">
                        <span className="text-[10px] text-gray-500 uppercase italic">PISTA</span>
                        <span className="text-xl text-white">{courtNumber}</span>
                    </div>
                </div>
                <div className="p-8 flex items-center justify-center flex-1">
                     <span className="text-gray-500 text-xs uppercase tracking-widest italic flex items-center gap-2">
                        <span className="animate-spin">⏳</span> ESPERANDO PARTIDO...
                     </span>
                </div>
            </div>
        );
    }

    const {
        courtNumber: dataCourtNumber,
        pair1,
        pair2,
        scorePair1: initialScore1,
        scorePair2: initialScore2,
        status,
        id: matchId
    } = court;

    // Estado local de marcadores (WPT Style)
    const [localScore1, setLocalScore1] = useState("0"); 
    const [localScore2, setLocalScore2] = useState("0");
    const [setScores, setSetScores] = useState({
        s1: { p1: 0, p2: 0 },
        s2: { p1: 0, p2: 0 },
        s3: { p1: 0, p2: 0 }
    });
    const [currentSet, setCurrentSet] = useState(1);
    const [servingPair, setServingPair] = useState(1);
    const [scoringMode, setScoringMode] = useState('gold'); // 'gold' o 'advantage'
    const [history, setHistory] = useState([]); // Historial de estados para Undo
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Mapeo de puntos oficiales de Pádel
    const PADEL_SEQ = ["0", "15", "30", "40", "Ad"];

    // Determinar si estamos en tiebreak (6-6 en el set actual)
    const isTiebreak = () => {
        const games = setScores[`s${currentSet}`];
        return games.p1 === 6 && games.p2 === 6;
    };

    // Sincronizar con props (opcional, dependiendo de si quieres que los puntos vengan de DB)
    useEffect(() => {
        // En este modo, el score total se guarda, pero los puntos son volátiles por juego
        // setLocalScore1(initialScore1 || 0);
    }, [initialScore1]);

    // Obtener color de estado
    const getStatusColor = () => {
        switch (status) {
            case 'in_progress':
                return 'bg-primary-500';
            case 'scheduled':
                return 'bg-success';
            case 'finished':
                return 'bg-gray-400';
            default:
                return 'bg-gray-300';
        }
    };

    // Obtener texto de estado
    const getStatusText = () => {
        switch (status) {
            case 'in_progress':
                return 'En juego';
            case 'scheduled':
                return 'Pendiente';
            case 'finished':
                return 'Finalizado';
            default:
                return 'Esperando';
        }
    };

    // Manejador para finalizar partido
    const handleFinish = async () => {
        if (localScore1 === localScore2) {
            alert('No puede haber empate. Debe haber un ganador.');
            return;
        }

        setIsSubmitting(true);

        try {
            await onFinishMatch({
                matchId,
                courtNumber,
                scorePair1: localScore1,
                scorePair2: localScore2
            });
        } catch (error) {
            console.error('Error al finalizar partido:', error);
            alert('Error al guardar el resultado. Inténtalo de nuevo.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Manejador para iniciar partido
    const handleStart = async () => {
        setIsSubmitting(true);

        try {
            await onStartMatch({
                matchId,
                courtNumber
            });
        } catch (error) {
            console.error('Error al iniciar partido:', error);
            alert('Error al iniciar el partido. Inténtalo de nuevo.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- LÓGICA DE UNDO (DESHACER) ---

    // Guardar estado actual en el historial
    const saveState = () => {
        const currentState = {
            localScore1,
            localScore2,
            setScores,
            currentSet,
            servingPair,
            scoringMode
        };
        // Limitamos el historial a los últimos 10 movimientos para no saturar memoria innecesariamente
        setHistory(prev => [...prev.slice(-9), currentState]);
    };

    // Restaurar el último estado
    const undoLastAction = () => {
        if (history.length === 0) return;

        const previousState = history[history.length - 1];
        
        // Restaurar valores
        setLocalScore1(previousState.localScore1);
        setLocalScore2(previousState.localScore2);
        setSetScores(previousState.setScores);
        setCurrentSet(previousState.currentSet);
        setServingPair(previousState.servingPair);
        setScoringMode(previousState.scoringMode);

        // Eliminar el estado restaurado del historial
        setHistory(prev => prev.slice(0, -1));

        // Sync Backend (Simulado por ahora con update de puntos)
        // NOTA: Esto solo actualiza puntos visuales en BD, no sets completos si estos cambiaron.
        // Para una implementación perfecta requeriríamos un endpoint `updateFullMatchState`.
        // Sin embargo, para el uso visual en vivo, esto ayuda.
        if (onUpdateScore) {
             // Sincronizamos ambos para asegurar
             onUpdateScore(courtNumber, 1, previousState.localScore1);
             onUpdateScore(courtNumber, 2, previousState.localScore2);
        }
    };

    // Función para manejar el tanteo oficial (15, 30, 40, Ad, Juego + Tiebreak)
    const adjustScore = (pairNumber) => {
        if (status === 'finished') return;

        // GUARDAR ESTADO ANTES DE CAMBIAR NADA
        saveState();

        // LÓGICA DE TIEBREAK (1, 2, 3...)
        if (isTiebreak()) {
            const p1 = parseInt(localScore1) || 0;
            const p2 = parseInt(localScore2) || 0;
            let gameFinished = false;

            if (pairNumber === 1) {
                const nextP1 = p1 + 1;
                if (nextP1 >= 7 && (nextP1 - p2) >= 2) {
                    addGame(1, true); // Pasar flag de tiebreak
                    resetPoints();
                    gameFinished = true;
                } else {
                    setLocalScore1(nextP1.toString());
                }
            } else {
                const nextP2 = p2 + 1;
                if (nextP2 >= 7 && (nextP2 - p1) >= 2) {
                    addGame(2, true); // Pasar flag de tiebreak
                    resetPoints();
                    gameFinished = true;
                } else {
                    setLocalScore2(nextP2.toString());
                }
            }

            // LÓGICA DE SAQUE EN TIEBREAK: Cambia tras el 1er punto, y luego cada 2 puntos.
            // Regla: Se cambia de saque cuando la suma de puntos es IMPAR.
            if (!gameFinished) {
                const totalPoints = (pairNumber === 1 ? p1 + 1 : p1) + (pairNumber === 2 ? p2 + 1 : p2);
                if (totalPoints % 2 !== 0) {
                    setServingPair(prev => prev === 1 ? 2 : 1);
                }
            }
            return;
        }

        // LÓGICA NORMAL (0, 15, 30, 40, Ad)
        const myPoints = pairNumber === 1 ? localScore1 : localScore2;
        const otherPoints = pairNumber === 1 ? localScore2 : localScore1;

        if (myPoints === "40") {
            if (otherPoints === "40") {
                if (scoringMode === 'gold') {
                    // PUNTO DE ORO: Gana directamente
                    addGame(pairNumber);
                    resetPoints();
                } else {
                    // VENTAJA (Advantage)
                    pairNumber === 1 ? setLocalScore1("Ad") : setLocalScore2("Ad");
                }
            } else if (otherPoints === "Ad") {
                // El otro pierde ventaja -> Vuelven a Deuce
                setLocalScore1("40");
                setLocalScore2("40");
            } else {
                // Gana juego directo (Tras 40-0, 40-15 o 40-30)
                addGame(pairNumber);
                resetPoints();
            }
        } else if (myPoints === "Ad") {
            // Gana juego tras ventaja
            addGame(pairNumber);
            resetPoints();
        } else {
            // Secuencia normal
            const idx = PADEL_SEQ.indexOf(myPoints);
            const next = PADEL_SEQ[idx + 1];
            pairNumber === 1 ? setLocalScore1(next) : setLocalScore2(next);
        }
    };

    const resetPoints = () => {
        setLocalScore1("0");
        setLocalScore2("0");
    };

    // Función para añadir juego al set actual
    const addGame = (pairNumber, fromTiebreak = false) => {
        // En Tiebreak, el saque para el SIGUIENTE set lo tiene quien recibió el 1er punto del tiebreak.
        // Como implementamos el cambio cada 2 puntos, el servidor final del tiebreak NO define el del siguiente set.
        // Regla: El que empezó sirviendo el tiebreak, RECIBE en el primer juego del siguiente set.
        
        setSetScores(prev => {
            const setKey = `s${currentSet}`;
            const currentSetData = prev[setKey];
            const newScores = {
                ...prev,
                [setKey]: {
                    ...currentSetData,
                    [pairNumber === 1 ? 'p1' : 'p2']: currentSetData[pairNumber === 1 ? 'p1' : 'p2'] + 1
                }
            };

            const p1 = newScores[setKey].p1;
            const p2 = newScores[setKey].p2;
            
            // Determinar si el set ha terminado
            const isSetOver = (p1 >= 6 && p1 - p2 >= 2) || p1 === 7 || (p2 >= 6 && p2 - p1 >= 2) || p2 === 7;

            // Manejo de rotación de saque
            if (fromTiebreak) {
                // Si viene de tiebreak, el servidor del 1er juego del set es el que recibió el 1er punto.
                // Guardamos el estado del servidor inicial del tiebreak para invertirlo.
                // NOTA: Para simplificar, si termina set en tiebreak, rotamos respecto a quien empezó sirviéndolo.
                setServingPair(prev => prev === 1 ? 2 : 1);
            } else {
                // Juego normal: Rotación simple
                setServingPair(prev => prev === 1 ? 2 : 1);
            }

            if (isSetOver) {
                if (currentSet < 3) setCurrentSet(currentSet + 1);
            }

            return newScores;
        });

        // Actualizar totalMatch para DB (opcional)
        if (onUpdateScore) {
            onUpdateScore(courtNumber, pairNumber, (pairNumber === 1 ? localScore1 : localScore2) + 1);
        }
    };

    return (
        <div className="bg-[#1A1D2B] rounded-xl overflow-hidden shadow-[0_0_25px_rgba(255,255,255,0.12)] border border-[#252A3D] flex flex-col transition-all hover:scale-[1.01] hover:shadow-[0_0_35px_rgba(255,255,255,0.2)]">
            {/* Header / Court Status - WPT Dark Bar */}
            <div className={`px-4 py-1.5 flex justify-between items-center bg-[#0F111A] border-b border-[#252A3D]`}>
                 <div className="flex items-center gap-1.5 font-black italic tracking-tighter">
                    <span className="text-[10px] text-gray-500 uppercase italic">PISTA</span>
                    <span className="text-xl text-white">{courtNumber}</span>
                </div>
                <div className="flex items-center gap-2">
                    {/* Status Indicator */}
                    {status === 'in_progress' ? (
                        <div className="flex items-center gap-1.5 animate-pulse">
                            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_8px_cyan]" />
                            <span className="text-[8px] font-black uppercase tracking-widest text-cyan-400">EN JUEGO</span>
                        </div>
                    ) : (
                        <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded ${status === 'scheduled' ? 'bg-yellow-500/10 text-yellow-500' : 'bg-gray-700 text-gray-400'}`}>
                            {getStatusText()}
                        </span>
                    )}
                </div>
            </div>

            {/* Header Row - Legends */}
            <div className="flex bg-[#0F111A] border-b border-[#252A3D] text-[6px] md:text-[7px]">
                <div className="flex-1 min-w-0"></div>
                <div className="w-[11%] min-w-[32px] font-black text-gray-500 uppercase flex items-center justify-center p-0.5 italic tracking-tighter">S1</div>
                <div className="w-[11%] min-w-[32px] font-black text-gray-500 uppercase flex items-center justify-center p-0.5 border-l border-[#252A3D] italic tracking-tighter">S2</div>
                <div className="w-[11%] min-w-[32px] font-black text-gray-500 uppercase flex items-center justify-center p-0.5 border-l border-[#252A3D] italic tracking-tighter">S3</div>
                <div className="w-[14%] min-w-[40px] font-black text-cyan-400 uppercase flex items-center justify-center p-0.5 border-l border-[#252A3D] italic tracking-tighter">PTS</div>
            </div>

            {/* Scoreboard - WPT Style Table */}
            <div className="flex flex-col border-b border-[#0F111A]">
                {/* Team 1 Row */}
                <div className="flex h-12 md:h-14 border-b border-[#0F111A]">
                    <div className="flex-1 min-w-0 bg-[#1A1D2B] flex items-center px-2 md:px-3 relative overflow-hidden group" onClick={() => setServingPair(1)}>
                         {/* Serve Indicator */}
                        {servingPair === 1 && <span className="absolute left-0.5 w-1.5 h-1.5 md:w-2 md:h-2 bg-yellow-400 rounded-full shadow-[0_0_8px_yellow]"></span>}
                        <div className="flex flex-col min-w-0 pl-2">
                            <h4 className="text-[10px] md:text-xs font-black text-white uppercase italic truncate tracking-tight leading-tight">
                                {pair1?.player1?.split(' ')[0]} / {pair1?.player2?.split(' ')[0]}
                            </h4>
                            <span className="text-[7px] md:text-[8px] font-bold text-gray-500 uppercase tracking-tighter italic">NIV {pair1?.averageLevel || 'N/A'}</span>
                        </div>
                    </div>
                    {/* SETS (Teal) */}
                    {[1, 2, 3].map(num => (
                        <div key={num} className={`w-[11%] min-w-[32px] shrink-0 border-l border-[#0F111A] flex items-center justify-center transition-colors ${currentSet === num ? 'bg-cyan-500 shadow-[inset_0_0_15px_rgba(0,0,0,0.3)]' : 'bg-cyan-400/70'}`}>
                            <span className={`text-lg md:text-2xl font-black italic ${currentSet === num ? 'text-white' : 'text-[#0F111A]'}`}>{setScores[`s${num}`].p1}</span>
                        </div>
                    ))}
                    {/* POINTS (White) */}
                    <div className="w-[14%] min-w-[40px] shrink-0 bg-white border-l border-[#0F111A] flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors active:bg-gray-100"
                        onClick={() => status !== 'finished' && adjustScore(1)}>
                        <span className="text-lg md:text-2xl font-black text-[#0F111A] italic">{localScore1}</span>
                    </div>
                </div>

                {/* Team 2 Row */}
                <div className="flex h-12 md:h-14">
                    <div className="flex-1 min-w-0 bg-[#1A1D2B] flex items-center px-2 md:px-3 relative group" onClick={() => setServingPair(2)}>
                        {/* Serve Indicator */}
                        {servingPair === 2 && <span className="absolute left-0.5 w-1.5 h-1.5 md:w-2 md:h-2 bg-yellow-400 rounded-full shadow-[0_0_8px_yellow]"></span>}
                        <div className="flex flex-col min-w-0 pl-2">
                            <h4 className="text-[10px] md:text-xs font-black text-white uppercase italic truncate tracking-tight leading-tight">
                                {pair2?.player1?.split(' ')[0]} / {pair2?.player2?.split(' ')[0]}
                            </h4>
                            <span className="text-[7px] md:text-[8px] font-bold text-gray-500 uppercase tracking-tighter italic">NIV {pair2?.averageLevel || 'N/A'}</span>
                        </div>
                    </div>
                    {/* SETS (Teal) */}
                    {[1, 2, 3].map(num => (
                        <div key={num} className={`w-[11%] min-w-[32px] shrink-0 border-l border-[#0F111A] flex items-center justify-center transition-colors ${currentSet === num ? 'bg-cyan-500 shadow-[inset_0_0_15px_rgba(0,0,0,0.3)]' : 'bg-cyan-400/70'}`}>
                            <span className={`text-lg md:text-2xl font-black italic ${currentSet === num ? 'text-white' : 'text-[#0F111A]'}`}>{setScores[`s${num}`].p2}</span>
                        </div>
                    ))}
                    {/* POINTS (White) */}
                    <div className="w-[14%] min-w-[40px] shrink-0 bg-white border-l border-[#0F111A] flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors active:bg-gray-100"
                        onClick={() => status !== 'finished' && adjustScore(2)}>
                        <span className="text-lg md:text-2xl font-black text-[#0F111A] italic">{localScore2}</span>
                    </div>
                </div>
            </div>

            {/* Actions Bar & Scoring Selector */}
            <div className="p-2 bg-[#0F111A] flex flex-col gap-2">
                {/* Scoring Mode Selector */}
                <div className="flex bg-[#1A1D2B] rounded p-0.5 border border-gray-800">
                    <button 
                        onClick={() => setScoringMode('gold')}
                        className={`flex-1 text-[9px] font-black py-1 rounded transition-all ${scoringMode === 'gold' ? 'bg-yellow-500 text-[#0F111A]' : 'text-gray-500'}`}
                    >
                        PUNTO DE ORO
                    </button>
                    <button 
                        onClick={() => setScoringMode('advantage')}
                        className={`flex-1 text-[9px] font-black py-1 rounded transition-all ${scoringMode === 'advantage' ? 'bg-white text-[#0F111A]' : 'text-gray-500'}`}
                    >
                        VENTAJA
                    </button>
                </div>

                {status !== 'in_progress' && status !== 'finished' && (
                    <button
                        onClick={handleStart}
                        disabled={isSubmitting}
                        className="w-full bg-green-600 hover:bg-green-700 text-white font-black py-2.5 px-4 rounded uppercase italic tracking-widest text-sm shadow-lg transition-all flex items-center justify-center gap-2 transform active:scale-95"
                    >
                        {isSubmitting ? <span className="animate-spin">⏳</span> : <span>▶️ INICIAR PARTIDO</span>}
                    </button>
                )}

                {status === 'in_progress' && (
                    <button
                        onClick={handleFinish}
                        disabled={isSubmitting || localScore1 === localScore2}
                        className="w-full bg-primary-600 hover:bg-primary-700 text-white font-black py-2 px-4 rounded uppercase italic tracking-widest text-xs transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {isSubmitting ? <span className="animate-spin">⏳</span> : <span>✓ CONFIRMAR RESULTADO</span>}
                    </button>
                )}

                {/* BOTÓN DESHACER (UNDO) */}
                {history.length > 0 && status !== 'finished' && (
                    <button
                        onClick={undoLastAction}
                        className="w-full bg-gray-600 hover:bg-gray-500 text-white font-black py-1.5 px-4 rounded uppercase italic tracking-widest text-xs transition-all flex items-center justify-center gap-2 transform active:scale-95"
                    >
                        ↩️ DESHACER ÚLTIMO PUNTO
                    </button>
                )}

                {status === 'finished' && (
                    <div className="w-full bg-[#1A1D2B] text-gray-500 font-black py-2 px-4 rounded uppercase italic tracking-widest text-[10px] text-center border border-gray-800">
                        FINALIZADO
                    </div>
                )}
            </div>
        </div>
    );
};

// Skeleton loader
LiveCourtCard.Skeleton = () => {
    return (
        <div className="bg-white rounded-xl shadow-card p-4 mb-4 border-l-4 border-gray-200 animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-24 mb-3" />
            <div className="space-y-3">
                <div className="bg-gray-100 rounded-lg p-3 h-20" />
                <div className="h-8 bg-gray-200 rounded w-16 mx-auto" />
                <div className="bg-gray-100 rounded-lg p-3 h-20" />
            </div>
            <div className="mt-4 h-12 bg-gray-200 rounded-lg" />
        </div>
    );
};

// Memoized to prevent re-renders unless props change (critical for timer performance)
const LiveCourtCardMemoized = React.memo(LiveCourtCard);

export default LiveCourtCardMemoized;
